/**
 * Infrastructure Node Profiles
 *
 * Profiles for infrastructure nodes (login, storage, management, gateway, etc.)
 */

import type { NodeProfile } from '../../types/additional-nodes';

export const DELL_R660_INFRA: NodeProfile = {
  id: 'dell-r660-infra',
  name: 'Dell PowerEdge R660 (Infrastructure)',
  role: 'infrastructure',
  vendor: 'Dell',
  model: 'PowerEdge R660',
  description: '1U single-socket infrastructure node',
  rackUnits: 1,
  powerKw: 0.5,
  ethernetPorts: 2,
  cpu: {
    sockets: 1,
    model: 'Intel Xeon Gold 6430',
    cores: 32,
  },
  memoryGB: 256,
  storageBays: 10,
  metadata: {
    notes: 'Compact 1U infrastructure server',
  },
};

export const SUPERMICRO_1U_INFRA: NodeProfile = {
  id: 'supermicro-1u-infra',
  name: 'Supermicro SuperServer 1U (Infrastructure)',
  role: 'infrastructure',
  vendor: 'Supermicro',
  model: 'SuperServer 1U',
  description: '1U single-socket infrastructure node',
  rackUnits: 1,
  powerKw: 0.4,
  ethernetPorts: 2,
  cpu: {
    sockets: 1,
    model: 'AMD EPYC 9354',
    cores: 32,
  },
  memoryGB: 384,
  storageBays: 10,
  metadata: {
    notes: 'Efficient AMD-based infrastructure platform',
  },
};

export const STORAGE_SERVER_2U: NodeProfile = {
  id: 'storage-2u',
  name: 'Storage Server 2U',
  role: 'infrastructure',
  vendor: 'Generic',
  model: 'Storage 2U',
  description: '2U high-density storage node',
  rackUnits: 2,
  powerKw: 0.8,
  ethernetPorts: 4,
  cpu: {
    sockets: 1,
    model: 'Intel Xeon Silver',
    cores: 16,
  },
  memoryGB: 128,
  storageBays: 24,
  metadata: {
    notes: 'High-capacity storage server',
  },
};

export const LOGIN_NODE: NodeProfile = {
  id: 'login-node',
  name: 'Login/Gateway Node',
  role: 'infrastructure',
  vendor: 'Generic',
  model: 'Login Node',
  description: '1U login and gateway node',
  rackUnits: 1,
  powerKw: 0.3,
  ethernetPorts: 2,
  cpu: {
    sockets: 1,
    model: 'Intel Xeon',
    cores: 16,
  },
  memoryGB: 64,
  metadata: {
    notes: 'User login and job submission node',
  },
};

export const MANAGEMENT_NODE: NodeProfile = {
  id: 'management-node',
  name: 'Management/Monitoring Node',
  role: 'infrastructure',
  vendor: 'Generic',
  model: 'Management Node',
  description: '1U cluster management and monitoring node',
  rackUnits: 1,
  powerKw: 0.3,
  ethernetPorts: 2,
  cpu: {
    sockets: 1,
    model: 'Intel Xeon',
    cores: 16,
  },
  memoryGB: 128,
  metadata: {
    notes: 'Cluster orchestration, monitoring, and management',
  },
};

export const CUSTOM_INFRA: NodeProfile = {
  id: 'custom-infra',
  name: 'Custom Infrastructure Node',
  role: 'infrastructure',
  vendor: 'Custom',
  model: 'Custom',
  description: 'Custom infrastructure node',
  rackUnits: 1,
  powerKw: 0.5,
  ethernetPorts: 2,
};

export const INFRASTRUCTURE_PROFILES: readonly NodeProfile[] = [
  DELL_R660_INFRA,
  SUPERMICRO_1U_INFRA,
  STORAGE_SERVER_2U,
  LOGIN_NODE,
  MANAGEMENT_NODE,
  CUSTOM_INFRA,
] as const;
